<template>
  <div class="pipe-cell">
    <!-- 当前状态 chip：悬停看三段分区，点击开二级弹窗 -->
    <el-tooltip :content="chip.tip || bucketTooltip" placement="top">
      <span class="pipe-chip" :class="chip.cls" @click="dialogVisible = true">
        <span class="dot"></span>{{ chip.text }}<span class="arr">▸</span>
      </span>
    </el-tooltip>

    <!-- 二级弹窗：三段进度条 + 三段详情 + 图片列表入口 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      width="720px"
      append-to-body
      custom-class="pipe-dialog"
    >
      <div slot="title" class="pd-title">
        批次 {{ batch.batch }}
        <el-tag :type="chip.tag" size="mini">{{ chip.text }}</el-tag>
        <span class="pd-count">{{ batch.image_count }} 张</span>
        <el-button
          v-if="acceptanceActionVisible"
          type="success"
          size="mini"
          :loading="accepting"
          @click="acceptBatch"
        >验收通过</el-button>
      </div>

      <div class="stagebar">
        <div class="prow">
          <template v-for="(s, i) in stages">
            <span :key="`n-${s.key}`" class="node" :class="s.state"></span>
            <span v-if="i < stages.length - 1" :key="`s-${s.key}`" class="seg" :class="s.state"></span>
          </template>
        </div>
        <div class="plabels">
          <span v-for="s in stages" :key="s.key" class="plabel" :class="s.state">{{ s.label }}</span>
        </div>
      </div>

      <div class="stage-detail" :class="stages[0].state">
        <div class="sd-head">
          <span class="idx">1</span>生成标注数据
          <el-tag :type="tagOf(stages[0]).type" size="mini">{{ tagOf(stages[0]).text }}</el-tag>
        </div>
        <div v-if="stage1Current" class="sd-body">
        <div class="line">
          pb {{ job.pb_done || 0 }}/{{ job.pb_total || 0 }}，图片 {{ batch.image_count }} 张<template
            v-if="job.task_count"
          >，登记 {{ job.task_count }} 条</template>
        </div>
        <div v-if="job.project_name" class="line">标注项目：{{ job.project_name }}</div>
        <div v-if="pendingImages > 0" class="line">
          待选图 <b>{{ pendingImages }}</b> 张<template v-if="excludedCount > 0">，已排除 {{ excludedCount }} 张</template>
        </div>
        <div v-if="importJob && importJob.error_message" class="err">{{ importJob.error_message }}</div>
        <div v-if="pendingImages > 0 && !isImporting" class="selection-entry">
          <el-button type="primary" size="mini" @click="openPicker">预览并选图</el-button>
          <span class="muted">默认全选，取消废帧后确认导入</span>
        </div>
        <div v-if="job.error_message" class="err">{{ job.error_message }}</div>
        </div>
        <div v-else class="sd-summary">
          pb {{ job.pb_done || 0 }}/{{ job.pb_total || 0 }} · 图片 {{ batch.image_count }} 张
          <template v-if="job.project_name"> · {{ job.project_name }}</template>
        </div>
      </div>

<div class="stage-detail" :class="stages[1].state">
        <div class="sd-head">
          <span class="idx">2</span>标注
          <el-tag :type="tagOf(stages[1]).type" size="mini">{{ tagOf(stages[1]).text }}</el-tag>
        </div>
        <div v-if="stage2Active" class="sd-body">
        <template v-if="batch.stage === 'prepending'">
          <div class="line muted">尚未开始预标注（{{ buckets.total }} 张）</div>
        </template>
        <template v-else-if="batch.stage === 'preannotating'">
          <div class="line muted">预标注中，结果回写后更新</div>
        </template>
        <template v-else>
          <div class="line">
            已人工标注 <b>{{ buckets.reviewed }}</b> · 待人工标注 <b>{{ buckets.total - buckets.reviewed }}</b>
            （共 {{ buckets.total }} 张）
          </div>
          <div class="line muted">
            预标注结果：有目标 <b>{{ buckets.pre }}</b> · 未发现目标 <b>{{ buckets.empty }}</b>
            · 漏产出结果 <b>{{ buckets.none }}</b>
          </div>
          <div class="mini-progress">
            <span class="p1" :style="{ width: pct(buckets.reviewed) }"></span>
            <span class="p2" :style="{ width: pct(buckets.pre) }"></span>
            <span class="p3" :style="{ width: pct(buckets.empty) }"></span>
          </div>
        </template>
        <div v-if="progressSteps.length" class="progress-steps">
          <span
            v-for="(step, i) in progressSteps"
            :key="step.key"
            class="progress-step"
            :class="step.state"
          >
            {{ step.text }}
            <span v-if="i < progressSteps.length - 1" class="progress-arrow">—</span>
          </span>
        </div>
        <div v-if="preannotateText" class="line">
          批量预标注：<span class="pre-status">{{ preannotateText }}</span>
          <template v-if="preannotateClamped || failExpanded">
            <el-button type="text" size="mini" @click="failExpanded = !failExpanded">
              {{ failExpanded ? '收起' : '展开全文' }}
            </el-button>
            <el-button type="text" size="mini" @click="copyFailDetail">复制全文</el-button>
          </template>
        </div>
        <!-- 错误只给摘要和结构化详情，日志去 docker logs 看 -->
        <div v-if="preJob && (preJob.error || preJob.error_message)" class="error-line">
          <span class="err-title">⚠ {{ (preJob.error && preJob.error.summary) || preJob.error_message }}</span>
          <el-button type="text" size="mini" @click="errorDetailVisible = true">错误详情</el-button>
        </div>
        <div v-if="preclassModelText" class="line">本批模型：{{ preclassModelText }}</div>
        <div v-if="preclassText" class="line">本批类别：{{ preclassText }}</div>
        <div v-if="receiptNote" class="line note">{{ receiptNote }}</div>
        <!-- 待预标注：就在批次弹窗里选参数并开始，不再另开弹窗 -->
        <div v-if="prestartVisible" class="retry-box">
          <div class="warn-text">图已导入，{{ buckets.none }} 张尚未预标注</div>
          <div class="retry-opts">
            <span class="muted">模型</span>
            <el-select v-model="retry.modelId" size="mini" style="width: 220px" :loading="retryModels.loading">
              <el-option label="内置 SAM3（默认）" value="" />
              <el-option v-for="m in retryModels.list" :key="m.id" :label="retryModelLabel(m)" :value="m.id" />
            </el-select>
            <template v-if="retryModelFramework === 'yolo'">
              <span class="muted">置信度</span>
              <el-input-number
                v-model="retry.confidence"
                :min="0.01"
                :max="0.99"
                :step="0.01"
                :precision="2"
                size="mini"
                style="width: 100px"
              />
            </template>
            <el-button type="warning" size="mini" :loading="starting" @click="submitStartPreannotate">开始预标注</el-button>
          </div>
          <div class="retry-opts">
            <el-button type="text" size="mini" @click="openPromptEditor">改类别/提示词…</el-button>
            <span v-if="retry.overrides" class="muted">本次按改后的 {{ retry.overrideCount }} 个类别派发</span>
            <el-checkbox v-model="retry.useRegistry">用最新类别注册表</el-checkbox>
          </div>
          <div class="hint">一旦派发，原批次不能再重选图，只能另存新批次。</div>
        </div>
        <!-- 预标注缺口重试；全覆盖但有未发现目标时也给出口（漏检复核：改提示词后重跑那几张） -->
        <div v-if="retryBoxVisible" class="retry-box">
          <div class="warn-text">{{ prefailText }}</div>
          <div v-if="injectOk" class="retry-opts">
            <span class="muted">推理已完成，可直接补注入（不重跑推理）</span>
            <el-button type="primary" size="mini" :loading="injecting" @click="retryInject">重试注入</el-button>
          </div>
          <div class="retry-opts">
            <el-radio-group v-model="retry.scope" size="mini">
              <el-radio-button v-if="buckets.none > 0" label="gap">重试漏产出结果 {{ buckets.none }} 张</el-radio-button>
              <el-radio-button v-if="buckets.empty > 0" label="empty">重跑未发现目标 {{ buckets.empty }} 张</el-radio-button>
              <el-radio-button label="all">全量重跑 {{ buckets.total }} 张</el-radio-button>
            </el-radio-group>
            <el-checkbox v-model="retry.useRegistry" class="reg-check">用最新类别注册表</el-checkbox>
            <el-button type="warning" size="mini" :loading="retrying" @click="retryPreannotate">重试预标注</el-button>
          </div>
          <div class="retry-opts">
            <span class="muted">模型</span>
            <el-select v-model="retry.modelId" size="mini" style="width: 220px" :loading="retryModels.loading">
              <el-option :label="`沿用本批模型${preclassModelText ? `（${preclassModelText}）` : ''}`" value="" />
              <el-option
                v-for="m in retryModels.list"
                :key="m.id"
                :label="retryModelLabel(m)"
                :value="m.id"
              />
            </el-select>
            <span v-if="retryModels.error" class="muted">{{ retryModels.error }}</span>
          </div>
          <div v-if="retryModelFramework === 'yolo'" class="retry-opts">
            <span class="muted">置信度</span>
            <el-input-number
              v-model="retry.confidence"
              :min="0.01"
              :max="0.99"
              :step="0.01"
              :precision="2"
              size="mini"
              style="width: 110px"
            />
            <span class="muted">
              默认使用本批值{{ preJob && preJob.preact_confidence != null ? `（${preJob.preact_confidence}）` : '（0.25）' }}，可直接修改
            </span>
          </div>
          <div class="retry-opts">
            <el-button type="text" size="mini" @click="openPromptEditor">改类别/提示词…</el-button>
            <span v-if="retry.overrides" class="muted">
              本次按改后的 {{ retry.overrideCount }} 个类别派发
              <el-button type="text" size="mini" class="undo-edit" @click="clearPromptEdits">撤销</el-button>
            </span>
          </div>
          <div class="hint">全量重跑会覆盖旧预测；已人工标注图不受影响。</div>
        </div>
        </div>
        <div v-else class="sd-summary">未开始</div>
      </div>

<div slot="footer" class="pd-foot">
        <el-dropdown trigger="click" @command="onMoreCommand">
          <el-button size="small">更多操作 ▾</el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="reselect" :disabled="!canReselect">重新选图</el-dropdown-item>
            <el-dropdown-item command="regenerate" :disabled="!canRegenerate">重建批次</el-dropdown-item>
            <el-dropdown-item command="history">批次历史</el-dropdown-item>
            <el-dropdown-item command="download" divided>下载本批</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <div class="pd-foot-right">
          <el-button size="small" :loading="refreshing" @click="refreshState">刷新状态</el-button>
          <el-button v-if="batch.ls_project_url" type="primary" size="small" @click="openLs">前往标注平台</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 重跑前就地改类别/提示词：只影响本次派发，勾选才写回注册表 -->
    <el-dialog
      title="编辑预标注类别与提示词"
      :visible.sync="promptDialog.visible"
      width="720px"
      append-to-body
    >
      <div class="muted prompt-hint">
        多个提示词用 <b>|</b> 分隔，留空时使用类别名。取消勾选表示本次不派发该类别。
        修改仅对本次生效，勾选后写回注册表。
      </div>
      <el-table :data="promptDialog.rows" size="mini" border max-height="320" style="width:100%">
        <el-table-column label="参与" width="60">
          <template slot-scope="{ row }">
            <el-checkbox v-model="row.enabled" />
          </template>
        </el-table-column>
        <el-table-column prop="name" label="类别" width="150" show-overflow-tooltip />
        <el-table-column label="提示词">
          <template slot-scope="{ row }">
            <el-input v-model="row.prompt" size="mini" placeholder="留空使用类别名" />
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-checkbox v-model="promptDialog.syncRegistry">写回类别注册表</el-checkbox>
        <el-button size="small" @click="promptDialog.visible = false">取消</el-button>
        <el-button type="primary" size="small" @click="applyPromptEdits">应用</el-button>
      </div>
    </el-dialog>

    <!-- 错误详情：只看原因/阶段/诊断，不展示日志 -->
    <el-dialog
      title="预标注错误详情"
      :visible.sync="errorDetailVisible"
      width="520px"
      append-to-body
    >
      <div v-if="preJob && (preJob.error || preJob.error_message)" class="error-detail">
        <div class="detail-row"><span>阶段</span><b>{{ errorStageText }}</b></div>
        <div class="detail-row"><span>错误</span><b>{{ (preJob.error && preJob.error.summary) || preJob.error_message }}</b></div>
        <div v-if="preJob.error && preJob.error.code" class="detail-row"><span>错误码</span><b>{{ preJob.error.code }}</b></div>
        <div v-if="preJob.error && preJob.error.detail" class="detail-row"><span>说明</span><b>{{ preJob.error.detail }}</b></div>
        <div v-if="preJob.error && preJob.error.attempt" class="detail-row"><span>尝试</span><b>第 {{ preJob.error.attempt }} 次</b></div>
        <div v-if="preJob.error && preJob.error.retry_at" class="detail-row"><span>下次重试</span><b>{{ preAnnotateAt(preJob.error.retry_at) }}</b></div>
        <pre v-if="errorDiagnosticsText" class="diag-box">{{ errorDiagnosticsText }}</pre>
      </div>
      <div slot="footer">
        <el-button size="small" @click="errorDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 抽帧后选图：默认全选，取消废帧后导入标注平台 -->
    <dataset-image-picker
      :visible.sync="pickerVisible"
      :job-id="(job || {}).id || ''"
      :batch="batch.batch"
      @confirmed="onSelectionConfirmed"
    />

    <!-- 批次重做：重新选图 / 重新生成 / 删除（破坏性动作要求输入批次名） -->
    <el-dialog
      :title="adminTitle"
      :visible.sync="admin.visible"
      :width="admin.kind === 'reselect' ? '480px' : '720px'"
      append-to-body
      :close-on-click-modal="false"
    >
      <div v-if="admin.loading" class="admin-loading">正在读取批次状态…</div>
      <template v-else-if="admin.info">
        <template v-if="admin.kind === 'reselect'">
          <div class="admin-lead">
            本批 {{ admin.info.images }} 张将全部退回待选，可重新勾选要标注的图片。
          </div>
          <div class="admin-hint">确认后，标注任务会按新的选择更新。</div>
        </template>

        <template v-else-if="admin.kind === 'regenerate'">
          <el-form label-width="96px" size="small" class="admin-form">
            <el-form-item label="抽帧时间段">
              <div v-if="admin.preview.loading" class="admin-hint">pb 预览加载中…</div>
              <div v-else-if="admin.preview.error" class="admin-hint">{{ admin.preview.error }}</div>
              <template v-else-if="admin.preview.items.length">
                <div class="admin-film">
                  <div
                    v-for="(item, i) in admin.preview.items"
                    :key="item.pb_file_name"
                    class="admin-film-item"
                    :class="{ selected: admin.preview.selected.includes(i) }"
                    :title="item.pb_file_name"
                    @click="toggleAdminFilm(i)"
                  >
                    <img v-if="item.keyframe_id" :src="filmImageSrc(item.keyframe_id)" />
                    <div v-else class="admin-film-ph">{{ i + 1 }}</div>
                    <span v-if="admin.preview.selected.includes(i)" class="admin-film-check">✓</span>
                  </div>
                </div>
                <div class="admin-hint">
                  已选 {{ admin.preview.selected.length }} / {{ admin.preview.items.length }} 段，点缩略图切换
                  <el-button type="text" size="mini" @click="selectAllFilm(true)">全选</el-button>
                  <el-button type="text" size="mini" @click="selectAllFilm(false)">全不选</el-button>
                </div>
              </template>
              <div v-else class="admin-hint">该目录没有可预览的 pb 段，沿用原批次时间段</div>
            </el-form-item>
            <el-form-item label="抽帧策略">
              <el-radio-group v-model="admin.options.strategy">
                <el-radio label="interval">固定间隔</el-radio>
                <el-radio label="grouped">按秒分组</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item v-if="admin.options.strategy === 'interval'" label="抽帧间隔">
              <el-input-number v-model="admin.options.frame_step" :min="1" :max="1000" size="small" />
            </el-form-item>
            <el-form-item v-else label="每组张数">
              <el-input-number v-model="admin.options.samples_per_group" :min="1" :max="10" size="small" />
            </el-form-item>
            <el-form-item label="输出镜头">
              <el-checkbox-group v-model="admin.options.cameras">
                <el-checkbox label="front">front</el-checkbox>
                <el-checkbox label="rear">rear</el-checkbox>
                <el-checkbox label="left">left</el-checkbox>
                <el-checkbox label="right">right</el-checkbox>
              </el-checkbox-group>
            </el-form-item>
          </el-form>
          <div class="admin-hint">原批次保留，模型与类别沿用本批。</div>
        </template>

        <div v-if="admin.submitting" class="admin-hint">
          正在执行：删标注平台任务可能要几十秒，请勿关闭弹窗或重复提交…
        </div>
      </template>
      <div v-else-if="admin.error" class="err">{{ admin.error }}</div>

      <div slot="footer">
        <el-button size="small" @click="admin.visible = false">取消</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="admin.submitting"
          :disabled="!canSubmitAdmin"
          @click="submitAdmin"
        >{{ adminSubmitText }}</el-button>
      </div>
    </el-dialog>

    <!-- 批次历史：同一目录残留的旧批与 -rN 衍生批次，可直接清理 -->
    <el-dialog title="批次历史" :visible.sync="history.visible" width="640px" append-to-body>
      <el-table v-loading="history.loading" :data="history.rows" size="mini" border max-height="360">
        <el-table-column prop="batch" label="批次" min-width="200" show-overflow-tooltip />
        <el-table-column prop="image_count" label="图片" width="70" />
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <el-tag v-if="row.published" type="success" size="mini">已发布</el-tag>
            <el-tag v-else size="mini">{{ stageText(row.stage) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80">
          <template slot-scope="{ row }">
            <el-button
              type="text"
              size="mini"
              class="danger-text"
              :loading="history.removing === row.batch"
              :disabled="row.published || !!history.removing"
              @click="removeFromHistory(row)"
            >{{ history.removing === row.batch ? '删除中…' : '删除' }}</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button size="small" @click="history.visible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 下载本批（图 + 标注）的进度弹窗 -->
    <zip-download-dialog ref="zipDl" title="批次下载" />
  </div>
</template>

<script>
import { fetchDatasetBatchDownloadManifest, fetchDatasetClasses, fetchDatasetJob, refreshDatasetImages, fetchDatasetRefreshJob, retryDatasetPreannotate, fetchPreannotateModels, acceptDatasetBatch, fetchDatasetBatchList, fetchDatasetBatchInspect, reselectDatasetBatch, regenerateDatasetBatch, removeDatasetBatch, fetchDatasetPreview, startDatasetPreannotate, fetchPreannotateInjectable, injectDatasetPreannotate } from '../api/dataset';
import { getKeyframeImageUrl } from '../api/label';
import { PIPELINE_STATES } from '../utils/labels';
import { preAnnotateAt, preAnnotateState, preAnnotateStageText, preAnnotateReceiptText } from '../utils/preannotate';
import ZipDownloadDialog from '../components/ZipDownloadDialog.vue';
import DatasetImagePicker from '../components/DatasetImagePicker.vue';

/** 二级弹窗内任务快照的刷新间隔（仅在途时开） */
const JOB_POLL_MS = 3000;

/** 失败文案默认截断长度（详情区展开看全文；服务侧错误含日志尾巴，长文不截会撑破弹窗） */
const FAIL_PREVIEW_CHARS = 120;

export default {
  name: 'DatasetPipelineCell',

  components: { ZipDownloadDialog, DatasetImagePicker },

  props: {
    batch: { type: Object, required: true },
  },

  data() {
    return {
      dialogVisible: false,
      pickerVisible: false,
      zipping: false, // 打包本批防连点
      refreshing: false, // 催办刷新防连点
      retrying: false, // 重试预标注防连点
      accepting: false, // 验收防连点
      // 重试范围（默认只补缺口）、类别来源、本次临时改的提示词、换模型（空＝沿用本批）
      retry: {
        scope: 'gap',
        useRegistry: false,
        overrides: null,
        syncRegistry: false,
        overrideCount: 0,
        modelId: '',
        confidence: null, // YOLO 置信度：null＝沿用本批
      },
      retryModels: { loading: false, loaded: false, error: '', list: [] }, // 重试可选模型
      failExpanded: false, // 失败文案展开（默认只给预览，全文可展开/复制）
      errorDetailVisible: false,
      promptDialog: { visible: false, rows: [], syncRegistry: false }, // 提示词编辑弹窗
      // 弹窗内轮询拿到的任务快照（在途时比列表下发的新；终态后仍可读出结果）
      liveJob: null,
      livePreJob: null,
      liveImportJob: null,
      jobTimer: null,
      jobPollInFlight: false,
      busyEmitted: false, // 已上报给列表页的「弹窗打开」状态，防重复计数
      // 批次重做：kind=reselect|regenerate|remove；info 是后端 inspect 的可执行动作快照
      admin: {
        visible: false,
        kind: '',
        loading: false,
        submitting: false,
        error: '',
        info: null,
        options: { strategy: 'interval', frame_step: 10, samples_per_group: 2, cameras: [] },
        // 抽帧时间段：重选 pb 段时用胶片条，默认回填原批次的 pb 下标
        preview: { loading: false, error: '', items: [], selected: [] },
      },
      history: { visible: false, loading: false, rows: [], removing: '' },
      starting: false,
      // 注入失败后的轻量重试：先问服务是否还留着推理结果，有才显示入口
      injectProbe: { loading: false, ok: false },
      injecting: false,
      // 弹窗打开时取一次批次可执行动作，重做入口直接按它置灰，避免点开才发现不能做
      policy: { loading: false, info: null },
    };
  },

  computed: {
    job() {
      return this._pickJob(this.liveJob, this.batch.job) || {};
    },

    /** 预标注任务快照：在途任务优先；否则取创建时间最新的一条，避免上一轮失败记录盖住本轮 */
    preJob() {
      if (this._inFlight(this.livePreJob)) return this.livePreJob;
      const live = this.livePreJob;
      const snap = this.batch.preannotate_job;
      if (!live) return snap;
      if (!snap) return live;
      return new Date(live.created_at || 0) >= new Date(snap.created_at || 0) ? live : snap;
    },

    /** 选图后的导入任务快照；刚提交的任务优先，避免上一轮失败任务盖住新任务 */
    importJob() {
      if (this._inFlight(this.liveImportJob)) return this.liveImportJob;
      return this._pickJob(this.liveImportJob, this.batch.import_job);
    },

    isImporting() {
      return this._inFlight(this.importJob);
    },

    pendingImages() {
      return Number(this.batch.pending_import) || 0;
    },

    excludedCount() {
      return Number(this.batch.excluded_count) || 0;
    },

    adminTitle() {
      return {
        reselect: '重新选图',
        regenerate: '重建批次',
      }[this.admin.kind] || '批次操作';
    },

    adminSubmitText() {
      if (this.admin.kind === 'reselect') {
        return '确认退回';
      }
      return '重建批次';
    },

    canSubmitAdmin() {
      const { info, kind, options } = this.admin;
      if (!info || this.admin.loading || this.admin.submitting) return false;
      if (kind === 'reselect') return info.can.reselect_all;
      const segments = this.admin.preview.items.length === 0 || this.admin.preview.selected.length > 0;
      return info.can.regenerate_new && options.cameras.length > 0 && segments;
    },

    /** 标注状态四段分区（互斥，和为总数）：已人工标注 / 已预标注 / 未发现目标 / 无模型结果 */
    buckets() {
      const b = this.batch;
      const total = Number(b.image_count) || 0;
      const pre = Number(b.annotated_pre) || 0;
      const empty = Number(b.annotated_empty) || 0;
      const reviewed = Number(b.annotated_reviewed) || 0;
      return { total, pre, empty, reviewed, none: Math.max(0, total - pre - empty - reviewed) };
    },

    /** chip 悬停提示：四段互斥分区（斜杠分数只表示「已人工标注/总数」） */
    bucketTooltip() {
      const { total, pre, empty, reviewed, none } = this.buckets;
      return `已人工标注 ${reviewed} / 待人工标注 ${total - reviewed}（共 ${total} 张）`
        + ` · 待人工构成：有目标 ${pre} / 未发现目标 ${empty} / ${this.batch.stage === 'prepending' ? '待预标注' : '漏产出结果'} ${none}`;
    },

    /** 预标注入口出现时机：待预标注（开始）、预标注失败/缺口（重试）、人工阶段有未发现目标/漏产出 */
    retryBoxVisible() {
      return this.batch.stage === 'prefailed'
        || (this.batch.stage === 'awaiting' && (this.buckets.empty > 0 || this.buckets.none > 0));
    },

    /** 失败阶段：只有预标注任务失败时才有值，用于决定是否问“能不能只补注入” */
    failureStage() {
      const p = this.preJob;
      if (!p || p.status !== 'failed') return null;
      return (p.error && p.error.stage) || p.stage || null;
    },

    /** 服务端还留着推理结果时才显示「重试注入」 */
    injectOk() {
      return Boolean(this.injectProbe.ok);
    },

    /** 重做入口是否可用：以 inspect 为准；还没取到时先放开，点开弹窗会再校验 */
    canReselect() {
      if (this.policy.loading) return false;
      return this.policy.info ? this.policy.info.can.reselect_all : true;
    },

    canRegenerate() {
      if (this.policy.loading) return false;
      return this.policy.info ? this.policy.info.can.regenerate_new : true;
    },

    /** 阶段 1 是否当前：抽帧中或还在选图/导入时展开，其余压成一行摘要 */
    stage1Current() {
      if (this.job && ['pending', 'processing'].includes(this.job.status)) return true;
      return this.batch.stage === 'none' || this.batch.stage === 'selecting';
    },

    /** 阶段 2 是否已开始：导入完成（待预标注）之后才有内容 */
    stage2Active() {
      return ['prepending', 'preannotating', 'prefailed', 'awaiting', 'ready', 'in_set', 'removed']
        .includes(this.batch.stage);
    },

    /** 待预标注：给一个显式的「开始预标注」入口，参数在弹窗里选 */
    prestartVisible() {
      return this.batch.stage === 'prepending';
    },

    /** 标注阶段标签：一级阶段 + 当前二级/进度，避免「标注 0/84」看起来什么都没做 */
    annotLabel() {
      const { total, pre, empty, reviewed } = this.buckets;
      const stage = this.batch.stage;
      if (stage === 'prepending') return '待预标注';
      if (stage === 'unannotated' || stage === 'preannotating' || stage === 'prefailed') return '预标注';
      if (reviewed >= total) return `已人工标注 ${reviewed}/${total}`;
      if (reviewed === 0 && total > 0) return `待人工标注 ${total - reviewed}`;
      return `人工标注 ${reviewed}/${total}`;
    },

    /**
     * 当前阶段状态文案（chip 与弹窗标题 tag 共用）。
     * 词面从状态词表（PIPELINE_STATES）取，只 6 个状态词；发布去向（待发布/在数据集中/已剔除）
     * 不是状态词，只在这一列芯片的末段出现。进度/张数走「·」后缀，原因走悬停。
     * 标注段只读后端下发的 stage 单源做投影，这里不再自己判档。
     */
    acceptance() {
      return this.batch.acceptance || { accepted: false };
    },

    acceptanceActionVisible() {
      return ['ready', 'in_set', 'removed'].includes(this.batch.stage) && !this.acceptance.accepted;
    },

    chip() {
      const b = this.batch;
      const job = this.job;
      const { total, pre, empty, reviewed, none } = this.buckets;
      const stage = b.stage;
      const preJob = this.preJob;
      const S = PIPELINE_STATES;
      if (job && (job.status === 'pending' || job.status === 'processing')) {
        return {
          text: `抽帧中 · pb ${job.pb_done || 0}/${job.pb_total || 0}`,
          cls: 'blue pulse',
          tag: S.none.tag,
        };
      }
      // 任务报错（状态已翻或只带 error_message）都归「待生成」＋失败后缀，具体原因进悬停
      if ((job && job.status === 'failed') || (stage === 'unannotated' && job && job.error_message)) {
        return {
          text: '抽帧失败',
          cls: 'red',
          tag: S.none.tag,
          tip: job.error_message
            ? `失败原因：${job.error_message}（勾选本行重新推送，或打开弹窗看详情）`
            : '失败原因：勾选本行重新推送，或打开弹窗看详情',
        };
      }
      if (this.isImporting) {
        const j = this.importJob || {};
        const total = Number(j.pb_total) || 0;
        const done = Number(j.pb_done) || 0;
        return {
          text: `导入中${total ? ` ${done}/${total}` : ''}`,
          cls: 'blue pulse',
          tag: S.selecting.tag,
          tip: '正在把保留的图片导入标注平台',
        };
      }
      if (stage === 'selecting') {
        const ij = this.importJob || {};
        if (ij.status === 'failed') {
          return {
            text: '导入失败',
            cls: 'red',
            tag: S.selecting.tag,
            tip: ij.error_message
              ? `失败原因：${ij.error_message}（打开弹窗重新提交选图）`
              : '打开弹窗重新提交选图',
          };
        }
        return {
          text: `选图中 · ${this.pendingImages} 张待选`,
          cls: 'blue',
          tag: S.selecting.tag,
          tip: '抽帧完成，选图确认后导入标注平台并开始预标注',
        };
      }
      if (stage === 'preannotating') {
        // 生命周期状态统一由 preAnnotateState 给出；一级在状态列，chip 只放二级。
        const ps = preAnnotateState(preJob);
        const secondary = ps.stageText
          || ((ps.phase === 'pending' || ps.phase === 'dispatching') ? '排队中' : '预标注中');
        return {
          text: secondary,
          cls: 'blue pulse',
          tag: S.preannotating.tag,
          tip: ps.text || '登记完成，等预标注调度派发；详情见弹窗',
        };
      }
      if (stage === 'prefailed') {
        const covered = pre + empty + reviewed;
        const ps = preAnnotateState(preJob);
        const tips = [];
        if (preJob && preJob.status === 'failed') {
          tips.push(`最近一次预标注任务失败：${ps.summary}`);
          tips.push(
            covered > 0
              ? `已覆盖 ${covered}/${total}，缺 ${total - covered} 张`
              : '本次没有产出'
          );
        } else if (preJob && preJob.status === 'completed') {
          tips.push(`最近一次预标注任务已完成，仍有 ${total - covered} 张没标注`);
        } else {
          tips.push(`有 ${covered} 张已有模型结果但从未登记过预标注任务，缺 ${total - covered} 张`);
        }
        if (empty > 0) tips.push(`其中 ${empty} 张模型空结果待人工复核（非任务失败）`);
        tips.push('打开弹窗点「重试预标注」补齐');
        const secondary = preJob && preJob.status === 'failed' ? '预标注失败' : `漏产出结果 ${none} 张`;
        return {
          text: secondary,
          cls: 'orange',
          tag: S.prefailed.tag,
          tip: tips.join('；'),
        };
      }
      if (stage === 'awaiting') {
        const tip = this.bucketTooltip;
        // 一张未审时报待人工标注张数，而不是「已标 0/84」看起来什么都没做
        return reviewed === 0
          ? { text: `待人工标注 ${total - reviewed} 张`, cls: 'blue', tag: S.awaiting.tag, tip }
          : { text: `人工标注中 ${reviewed}/${total}`, cls: 'blue', tag: S.awaiting.tag, tip };
      }
      if (stage === 'prepending') {
        return {
          text: '待预标注',
          cls: 'blue',
          tag: S.prepending.tag,
          tip: '图已导入，点「开始预标注」后才跑模型；也可以继续重新选图',
        };
      }
      if (stage === 'unannotated') {
        return { text: '排队中', cls: '', tag: S.unannotated.tag };
      }
      if (stage === 'none') {
        return { text: '待生成', cls: '', tag: S.none.tag };
      }
      // 人工标注完成后，操作 chip 只讲验收二级状态；版本去向着独立「版本」列。
      return {
        text: this.acceptance.accepted ? '已验收' : '未验收',
        cls: 'green',
        tag: S.ready.tag,
        tip: '内部抽检未完成；版本管理在「数据集」页',
      };
    },

    /** 批量预标注统一状态对象（phase/label/tone/text/summary）。 */
    preannotateState() {
      return preAnnotateState(this.preJob);
    },

    /** 批量预标注状态全文：派发 / 阶段进度 / 起止 / 失败原因。 */
    preannotateFullText() {
      return this.preannotateState.text;
    },

    /** 本批预标注用的模型（登记任务时冻结；老任务没有快照按内置 SAM3） */
    preclassModelText() {
      const job = this.preJob || {};
      const model = job.preact_model;
      if (!model) return '';
      const parts = [model.name || model.id || ''];
      if (model.framework) parts.push(model.framework);
      const classCount = Array.isArray(model.class_names) ? model.class_names.length : 0;
      if (classCount > 0) parts.push(`${classCount} 类`);
      const confidence = Number(job.preact_confidence);
      if (model.framework === 'yolo' && Number.isFinite(confidence) && confidence > 0) {
        parts.push(`置信度 ${confidence}`);
      }
      return parts.filter(Boolean).join(' · ');
    },

    /** 重试当前选中的模型框架：空/未选＝沿用本批模型 */
    retryModelFramework() {
      const picked = this.retryModels.list.find(m => m.id === this.retry.modelId);
      const model = picked || (this.preJob || {}).preact_model || null;
      return model ? model.framework || null : null;
    },

    /** 状态行展示值：长文案默认只给预览，展开后给全文（服务侧错误可达 2000 字符） */
    preannotateText() {
      const state = this.preannotateState;
      if (this.failExpanded) return state.text;
      if (state.phase === 'failed') {
        // 失败阶段决定文案：下载/推理/注入/服务自检，避免把注入失败说成「漏产出」
        const job = this.preJob || {};
        const stage = (job.error && job.error.stage) || job.stage || '';
        const label = { health: '服务自检', downloading: '下载', inferring: '推理', injecting: '注入' }[stage];
        const short = `失败${label ? `（${label}）` : ''} · ${state.summary}`;
        return short.length <= FAIL_PREVIEW_CHARS ? short : `${short.slice(0, FAIL_PREVIEW_CHARS)}…`;
      }
      return state.text;
    },

    /** 是否截断了（决定「展开全文 / 复制」按钮出不出现） */
    preannotateClamped() {
      return !this.failExpanded && this.preannotateText.endsWith("…");
    },

    /** 服务端错误诊断：GPU/CUDA 环境等，原样以 JSON 展示 */
    errorDiagnosticsText() {
      const diagnostics = this.preJob && this.preJob.error && this.preJob.error.diagnostics;
      if (!diagnostics || Object.keys(diagnostics).length === 0) return '';
      return JSON.stringify(diagnostics, null, 2);
    },

    /** 下载/推理/注入三段进度；只展示 service 的 stage，不拿 pb_done 冒充进度。 */
    progressSteps() {
      const p = this.preJob || {};
      const order = ['downloading', 'inferring', 'injecting'];
      const labels = { downloading: '下载', inferring: '推理', injecting: '注入' };
      if (!p.stage || !order.includes(p.stage)) return [];
      const current = order.indexOf(p.stage);
      return order.map((key, i) => {
        let text = labels[key];
        let state = 'todo';
        if (p.status === 'completed' || i < current) {
          text = `${labels[key]} ✓`;
          state = 'done';
        } else if (i === current) {
          const total = Number(p.stage_total) || 0;
          const done = Number(p.stage_done) || 0;
          text = total > 0 ? `${labels[key]} ${done}/${total}` : labels[key];
          state = p.status === 'failed' ? 'failed' : 'current';
        } else {
          text = `${labels[key]} 等待`;
        }
        return { key, text, state };
      });
    },

    errorStageText() {
      const map = { downloading: '下载', inferring: '推理', injecting: '注入', health: '健康检查' };
      const e = (this.preJob && this.preJob.error) || {};
      return map[e.stage || (this.preJob || {}).stage] || e.stage || '-';
    },

    /** 本批预标注的类别+提示词快照（登记任务时冻结，不随注册表变动） */
    preclassText() {
      const classes = (this.preJob || {}).preact_classes;
      if (!Array.isArray(classes) || classes.length === 0) return '';
      // 提示词放到编辑弹窗里看；批次弹窗只列类别名，避免长文案撑乱状态区。
      return classes.map(c => c.name).filter(Boolean).join('、');
    },

    /** 完成回执行：写回 LS 的图数 + 未发现目标/未处理附注（结果是否落回标注平台看这行） */
    receiptNote() {
      return preAnnotateReceiptText((this.preJob || {}).receipt);
    },

    /** prefailed 的原因说明（重试框首行）：按最近任务状态与覆盖情况拆三种说法 */
    prefailText() {
      const { empty, none } = this.buckets;
      const ps = this.preannotateState;
      if (ps.phase === 'failed') return `预标注失败：${ps.summary}`;
      if (none > 0) return `漏产出结果 ${none} 张，可重试补齐`;
      if (empty > 0) return `有 ${empty} 张未发现目标，可改提示词后重跑`;
      return '本批尚未登记预标注任务';
    },

    /** 三段进度条（弹窗内）：节点状态 + 标签 */
    stages() {
      const b = this.batch;
      const job = this.job;
      const { total, reviewed } = this.buckets;

      let s1 = 'todo';
      if (job) {
        if (job.status === 'completed') s1 = job.error_message ? 'warn' : 'done';
        else if (job.status === 'failed') s1 = 'warn';
      }
      // 抽帧完成但还有图待选/正在导入：这一步没走完，保持进行中
      if (this.pendingImages > 0 || this.isImporting) s1 = 'cur';
      if (this.importJob && this.importJob.status === 'failed') s1 = 'warn';
      const list = [
        { key: 1, label: '生成标注数据', state: s1 },
        { key: 2, label: this.annotLabel, state: 'todo' },
      ];
      // 全部人工标注完才算标注阶段走完；版本发布只在「数据集」页，不参与这里的状态
      if (total > 0 && reviewed >= total) list[1].state = 'done';
      else if (b.stage === 'prefailed' || this.preannotateState.phase === 'failed') list[1].state = 'warn';
      // 第一个未完成的阶段标为「进行中」（有错误的阶段保持警告色）
      const idx = list.findIndex(s => s.state !== 'done');
      if (idx >= 0 && list[idx].state !== 'warn') list[idx].state = 'cur';
      return list;
    },
  },

  watch: {
    /** 二级弹窗开着才有必要刷任务快照：关闭即停；打开时按当前状态归位重试范围默认值 */
    failureStage(stage) {
      if (stage === 'injecting' && this.dialogVisible) this.probeInjectable();
      else if (stage !== 'injecting') this.injectProbe.ok = false;
    },

    dialogVisible(on) {
      if (on) {
        this.loadPolicy();
        this.injectProbe = { loading: false, ok: false };
        if (this.failureStage === 'injecting') this.probeInjectable();
        // 有缺口先补缺口；全覆盖但有未发现目标时默认重跑它们（漏检复核最常用）
        const { none, empty } = this.buckets;
        this.retry.scope = none > 0 ? 'gap' : (empty > 0 ? 'empty' : 'all');
        if (this.retry.confidence == null) this.retry.confidence = this.retryConfidenceValue();
        this.loadRetryModels();
        this.startJobPolling();
      } else {
        this.stopJobPolling();
      }
      this.syncBusy();
    },

    /** 选图弹窗同理：打开期间别让列表刷新把本组件连同弹窗卸载 */
    pickerVisible() {
      this.syncBusy();
    },

    /** 批次重做 / 历史记录弹窗也算「用户在操作」，开着时不刷列表 */
    'admin.visible'() {
      this.syncBusy();
    },

    'history.visible'() {
      this.syncBusy();
    },
  },

  created() {
    // 输入框始终写一个数字，避免 el-input-number 从 0.01 起；优先本批登记值
    this.retry.confidence = this.retryConfidenceValue();
  },

  beforeDestroy() {
    this.stopJobPolling();
    // 弹窗还开着就被销毁（行被刷掉/父级重渲染）：把 busy 计数还给父级，避免列表轮询永久暂停
    if (this.busyEmitted) this.$emit('busy', false);
  },

  methods: {
    /** 行内弹窗开关上报给列表页：有弹窗就暂停整表轮询（只在状态变化时上报一次） */
    syncBusy() {
      const on = !!(this.dialogVisible || this.pickerVisible
        || (this.admin && this.admin.visible) || (this.history && this.history.visible));
      if (on === this.busyEmitted) return;
      this.busyEmitted = on;
      this.$emit('busy', on);
    },
    /** 两个任务快照取更「新」的：终态 > 进行中 > 等待；同级优先本地轮询值（比列表刷新更勤） */
    _pickJob(live, prop) {
      const rank = j => {
        if (!j) return -1;
        if (j.status === 'completed' || j.status === 'failed') return 2;
        return j.status === 'processing' ? 1 : 0;
      };
      return rank(live) >= rank(prop) ? live || null : prop || null;
    },

    /** 任务是否在途（轮询开关与终态判定的唯一口径） */
    _inFlight(job) {
      return !!job && (job.status === 'pending' || job.status === 'processing');
    },

    /** 弹窗打开时若该批在途：每 3s 刷一次任务快照（关闭即停；无在途则保持现状） */
    startJobPolling() {
      this.stopJobPolling();
      if (
        !this._inFlight(this.batch.job)
        && !this._inFlight(this.batch.preannotate_job)
        && !this._inFlight(this.importJob)
      ) return;
      this.pollJobs();
      this.jobTimer = setInterval(() => this.pollJobs(), JOB_POLL_MS);
    },

    stopJobPolling() {
      if (this.jobTimer) {
        clearInterval(this.jobTimer);
        this.jobTimer = null;
      }
    },

    /** 拉一次在途任务快照；全部到达终态就停轮询并让列表重取（芯片回到列表数据驱动） */
    async pollJobs() {
      const targets = [];
      // 任务号缺失（异常摘要）不轮询：等列表下一轮带上 id 再查
      if (this._inFlight(this.batch.job) && this.batch.job.id) {
        targets.push({ key: 'liveJob', id: this.batch.job.id });
      }
      if (this._inFlight(this.batch.preannotate_job) && this.batch.preannotate_job.id) {
        targets.push({ key: 'livePreJob', id: this.batch.preannotate_job.id });
      }
      if (this._inFlight(this.importJob) && this.importJob.id) {
        targets.push({ key: 'liveImportJob', id: this.importJob.id });
      }
      if (targets.length === 0) {
        this.stopJobPolling();
        return;
      }
      if (this.jobPollInFlight || document.hidden) return;
      this.jobPollInFlight = true;
      try {
        const rows = [];
        const importWasInFlight = this._inFlight(this.importJob);
        for (const t of targets) {
          const row = (await fetchDatasetJob(t.id)).data;
          this[t.key] = t.key === 'livePreJob'
            ? this._mergePreProgress(this.livePreJob, row)
            : row;
          if (t.key === 'liveImportJob' && importWasInFlight && row.status === 'completed') {
            this.$message.success('导入成功');
          }
          rows.push(row);
        }
        if (rows.every(row => !this._inFlight(row))) {
          this.stopJobPolling();
          this.$emit('refresh');
        }
      } catch {
        // 单次查询失败不打断：下一轮接着试（列表侧本来也有轮询兜底）
      } finally {
        this.jobPollInFlight = false;
      }
    },

    tagOf(s) {
      if (s.key === 2 && this.batch.stage === 'prepending') {
        return { type: 'info', text: '待预标注' };
      }
      return {
        done: { type: 'success', text: '已完成' },
        cur: { type: '', text: '进行中' },
        warn: { type: 'warning', text: '有错误' },
        todo: { type: 'info', text: '未开始' },
      }[s.state];
    },

    pct(n) {
      const total = Number(this.batch.image_count) || 0;
      if (total === 0) return '0%';
      return `${((Number(n) || 0) / total) * 100}%`;
    },

    openLs() {
      if (this.batch.ls_project_url) window.open(this.batch.ls_project_url, '_blank');
    },

    /** 打开选图弹窗（抽帧任务为图源：只列本批待选的 pending 图） */
    openPicker() {
      this.pickerVisible = true;
    },

    /** 选图确认：拿导入任务快照切到「导入中」，并让列表重取批次汇总 */
    onSelectionConfirmed(data) {
      if (data && data.job) this.liveImportJob = data.job;
      this.$emit('refresh');
      this.startJobPolling();
    },

    /** Footer「更多操作」：重做、历史、下载、图片列表、删除 */
    onMoreCommand(command) {
      if (command === 'reselect') return this.openAdmin('reselect');
      if (command === 'regenerate') return this.openAdmin('regenerate');
      if (command === 'history') return this.openHistory();
      if (command === 'download') return this.downloadBatch();
    },

    /** 问服务端是否还有注入失败的推理结果；查不到/服务重启就静默不显示入口 */
    async probeInjectable() {
      if (this.injectProbe.loading) return;
      this.injectProbe.loading = true;
      try {
        const res = await fetchPreannotateInjectable(this.batch.batch);
        this.injectProbe.ok = Boolean(res.data && res.data.injectable);
      } catch (err) {
        this.injectProbe.ok = false;
      } finally {
        this.injectProbe.loading = false;
      }
    },

    /** 只补注入：不重新推理，服务端复用保留的 output */
    async retryInject() {
      if (this.injecting) return;
      this.injecting = true;
      try {
        await injectDatasetPreannotate(this.batch.batch);
        this.$message.success('已提交注入重试');
        this.injectProbe.ok = false;
        this.$emit('refresh');
      } catch (err) {
        // 拦截器已提示；结果被清理时会返回明确文案
      } finally {
        this.injecting = false;
      }
    },

    /** 开始预标注：就在批次弹窗内提交，参数此刻才确定（模型/类别/提示词/置信度） */
    async submitStartPreannotate() {
      if (this.starting) return;
      const { useRegistry, overrides, syncRegistry, modelId, confidence } = this.retry;
      const payload = { use_registry: useRegistry };
      if (modelId) payload.preact_model_id = modelId;
      if (confidence != null) payload.confidence = confidence;
      if (overrides) {
        payload.preact_classes = overrides;
        payload.sync_registry = syncRegistry;
      }
      this.starting = true;
      try {
        const res = await startDatasetPreannotate(this.batch.batch, payload);
        const d = res.data || {};
        const syncText = Number(d.registry_synced) > 0 ? `，已写回注册表 ${d.registry_synced} 类` : '';
        this.$message.success(`已开始预标注：候选 ${d.candidates || 0} 张${syncText}`);
        this.clearPromptEdits();
        this.$emit('refresh');
      } catch (err) {
        // 拦截器已提示
      } finally {
        this.starting = false;
      }
    },

    /** 批次可执行动作：弹窗打开时取一次，重做按钮据此置灰 */
    async loadPolicy() {
      this.policy.loading = true;
      try {
        const info = (await fetchDatasetBatchInspect(this.batch.batch, {
          dataset_id: this.batch.dataset_id,
        })).data;
        this.policy.info = info;
      } catch (err) {
        this.policy.info = null; // 取不到就放开入口，点开重做弹窗会再校验
      } finally {
        this.policy.loading = false;
      }
    },

    /** 打开批次重做弹窗：先取后端 inspect，按可执行动作预选默认项 */
    async openAdmin(kind) {
      if (kind === 'reselect' && !this.canReselect) return;
      if (kind === 'regenerate' && !this.canRegenerate) return;
      const a = this.admin;
      a.visible = true;
      a.kind = kind;
      a.loading = true;
      a.submitting = false;
      a.error = '';
      a.info = null;
      a.preview = { loading: false, error: '', items: [], selected: [] };
      try {
        const info = (await fetchDatasetBatchInspect(this.batch.batch, {
          dataset_id: this.batch.dataset_id,
        })).data;
        a.info = info;
        if (info.params) {
          a.options.strategy = info.params.strategy;
          a.options.frame_step = info.params.frame_step;
          a.options.samples_per_group = info.params.samples_per_group;
          a.options.cameras = [...(info.params.cameras || [])];
        }
        if (kind === 'regenerate') {
          await this.loadAdminPreview(
            (this.batch.job && this.batch.job.object_name) || '',
            info.params && info.params.pb_indexes,
          );
        }
      } catch (err) {
        a.error = (err && err.message) || '读取批次状态失败';
      } finally {
        a.loading = false;
      }
    },

    /** 抽帧时间段预览：默认回填原批次的 pb 下标，没限制过就全选 */
    async loadAdminPreview(objectName, pbIndexes) {
      const p = this.admin.preview;
      if (!objectName) {
        p.error = '缺少目录信息，沿用原批次时间段';
        return;
      }
      p.loading = true;
      p.error = '';
      p.items = [];
      p.selected = [];
      try {
        const res = await fetchDatasetPreview(objectName);
        p.items = (res.data && res.data.keyframes) || [];
        const wanted = Array.isArray(pbIndexes) && pbIndexes.length > 0
          ? pbIndexes.filter(i => Number.isInteger(i) && i >= 0 && i < p.items.length)
          : null;
        p.selected = wanted && wanted.length > 0
          ? [...new Set(wanted)]
          : p.items.map((_, i) => i);
      } catch (err) {
        p.error = 'pb 预览加载失败，沿用原批次时间段';
      } finally {
        p.loading = false;
      }
    },

    toggleAdminFilm(index) {
      const list = this.admin.preview.selected;
      const at = list.indexOf(index);
      if (at >= 0) list.splice(at, 1);
      else list.push(index);
    },

    selectAllFilm(on) {
      this.admin.preview.selected = on
        ? this.admin.preview.items.map((_, i) => i)
        : [];
    },

    filmImageSrc(keyframeId) {
      return `${getKeyframeImageUrl(keyframeId)}?w=240`;
    },

    async submitAdmin() {
      const a = this.admin;
      if (!this.canSubmitAdmin) return;
      const datasetId = this.batch.dataset_id;
      a.submitting = true;
      try {
        if (a.kind === 'reselect') {
          await reselectDatasetBatch(this.batch.batch, { dataset_id: datasetId });
          this.$message.success('已退回待选，可重新选图');
        } else {
          const res = await regenerateDatasetBatch(this.batch.batch, {
            dataset_id: datasetId,
            mode: 'new',
            options: {
              strategy: a.options.strategy,
              frame_step: a.options.frame_step,
              samples_per_group: a.options.samples_per_group,
              cameras: a.options.cameras,
              // 没加载到预览就交给后端沿用原批次快照，避免把时间段清空
              pb_indexes: a.preview.items.length > 0 ? a.preview.selected : undefined,
            },
          });
          this.$message.success(`已提交新批次 ${res.data.batch}`);
        }
        a.visible = false;
        this.$emit('refresh');
      } catch (err) {
        // 校验类错误由拦截器统一提示；超时要提醒「后台可能仍在跑」，避免用户误以为没生效
        if (err && (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || ''))) {
          this.$message.warning('请求超时：后台可能仍在执行，稍后刷新列表确认结果');
        }
      } finally {
        a.submitting = false;
      }
    },

    /** 同一目录的历史批次：旧批与 -rN 衍生批次都在这里清理 */
    async openHistory() {
      const h = this.history;
      h.visible = true;
      h.loading = true;
      h.rows = [];
      try {
        const objectName = (this.batch.job && this.batch.job.object_name) || '';
        const res = await fetchDatasetBatchList({
          object_name: objectName,
          dataset_id: this.batch.dataset_id,
        });
        h.rows = res.data || [];
      } catch (err) {
        this.$message.error((err && err.message) || '批次历史加载失败');
      } finally {
        h.loading = false;
      }
    },

    stageText(stage) {
      return (PIPELINE_STATES[stage] && PIPELINE_STATES[stage].label) || stage || '-';
    },

    async removeFromHistory(row) {
      try {
        await this.$confirm(
          `删除批次 ${row.batch}？图片、标注平台任务与对象存储文件会一并清理，不可恢复。`,
          '删除批次',
          { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' },
        );
      } catch (err) {
        return; // 取消
      }
      this.history.removing = row.batch;
      try {
        await removeDatasetBatch(row.batch, { dataset_id: this.batch.dataset_id });
        this.$message.success(`批次 ${row.batch} 已删除`);
        await this.openHistory();
        this.$emit('refresh');
      } catch (err) {
        // 拦截器已提示具体原因（如标注平台任务删不掉）；这里补一句“批次还在”，避免看起来像没生效
        if (err && err.message && !/已发布/.test(err.message)) {
          this.$message.warning(`批次 ${row.batch} 未被删除，可稍后重试`);
        }
      } finally {
        this.history.removing = '';
      }
    },

    /**
     * 本批打包下载：件数多（图 + 同名标注 txt），走流式 zip；清单在保存框之后才向后端要，
     * 免得后端签了一堆 URL 用户却取消了保存。
     */
    async downloadBatch() {
      if (this.zipping) return;
      this.zipping = true;
      try {
        const name = `${this.batch.batch}.zip`;
        const res = await this.$refs.zipDl.run(async () => {
          const manifest = (await fetchDatasetBatchDownloadManifest(this.batch.batch)).data || {};
          // 图在库里但对象存储里没有了：只提醒，不挡住其余文件
          if (Number(manifest.missing_images) > 0) {
            this.$message.warning(`${manifest.missing_images} 张图的文件在对象存储里不存在，已跳过`);
          }
          return manifest.files || [];
        }, name);
        // 保存框阶段取消等于什么都没发生，不用回列表；下过或下到一半中断才回列表对齐最新状态
        if (res.status === 'done' || (res.status === 'cancelled' && res.stage !== 'save')) {
          this.$emit('refresh');
        }
      } finally {
        this.zipping = false;
      }
    },

    /** 刷新状态：在途→代问预标注服务（只读秒回）；空闲→提交后台对账任务并轮询 */
    async acceptBatch() {
      if (this.accepting) return;
      this.accepting = true;
      try {
        await acceptDatasetBatch(this.batch.batch, this.batch.dataset_id);
        this.$message.success('已验收');
        this.$emit('refresh');
      } catch (err) {
        this.$message.error((err && err.message) || '验收失败');
      } finally {
        this.accepting = false;
      }
    },

    async refreshState() {
      if (this.refreshing) return;
      this.refreshing = true;
      try {
        const res = await refreshDatasetImages(this.batch.batch);
        const s = res.data || {};
        if (s.mode === 'remote') {
          this._applyRemoteStatus(s);
          this.$emit('refresh');
        } else if (s.jobId) {
          this.$message.info(`已提交后台同步（${s.total || 0} 张），完成后自动更新`);
          await this._pollRefreshJob(s.jobId);
        } else {
          this.$emit('refresh');
        }
      } catch {
        // 错误已由拦截器统一提示
      } finally {
        this.refreshing = false;
      }
    },

    async _pollRefreshJob(jobId) {
      if (!jobId) return;
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        let job;
        try {
          const res = await fetchDatasetRefreshJob(jobId);
          job = res.data || {};
        } catch {
          return; // 轮询失败就停在当前状态，别反复弹错
        }
        if (job.status === 'queued' || job.status === 'running') {
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        if (job.status === 'failed') {
          this.$message.error(job.error_message || '后台同步失败');
          return;
        }
        const s = job.stats || {};
        const tail = Number(s.missing) > 0 ? `，${s.missing} 张已被移除` : '';
        const back = Number(s.demoted) > 0 ? `，回退 ${s.demoted} 张（标注/预测被删）` : '';
        const truncated = s.truncated ? '，超出单次上限的部分由增量同步继续处理' : '';
        this.$message.success(
          `已核对 ${s.checked || job.processed || 0} 张：${s.reviewed || 0} 张已人工标注、` +
            `${s.pre || 0} 张已预标注、${s.empty || 0} 张未发现目标、${s.none || 0} 张漏产出结果${back}${tail}${truncated}`,
        );
        if (Number(s.failed) > 0) {
          this.$message.warning(`${s.failed} 张查询失败，稍后再试`);
        }
        this.$emit('refresh');
        return;
      }
      this.$message.warning('后台仍在同步，可稍后刷新页面查看');
    },

    /**
     * 在途：把代问回来的远端阶段进度即时上屏（只读，不落库）。
     * 只覆盖阶段字段，任务状态仍以平台 job 为准——终态由 15s 轮询写回，按钮不抢职责。
     */
    _applyRemoteStatus(s) {
      const p = this.preJob || {};
      if (s.phase === 'pending') {
        const at = preAnnotateAt(p.retry_at);
        this.$message.info(`预标注任务排队中${at ? `，约 ${at} 自动重试` : ''}`);
        return;
      }
      if (s.phase === 'dispatched') {
        this.$message.info('已派发至预标注服务，等待远端受理');
        return;
      }
      if (s.phase === 'unreachable') {
        this.$message.warning('预标注服务暂不可达，显示最近一次状态');
        return;
      }
      const r = s.remote || {};
      this.livePreJob = this._mergePreProgress(this.preJob, {
        ...p,
        stage: r.stage || null,
        stage_done: Number(r.stage_done) || 0,
        stage_total: r.stage_total == null ? null : Number(r.stage_total),
      });
      const stage = preAnnotateStageText(this.livePreJob);
      const total = Number(r.total) || 0;
      const downloaded = total > 0 ? `已下载 ${Number(r.done) || 0}/${total} 张` : '';
      if (r.status === 'failed') this.$message.warning('预标注服务已报失败，平台下一轮同步详情');
      else if (r.status === 'completed') this.$message.success('预标注服务已跑完，等待平台回写状态');
      else this.$message.success(`预标注进行中：${[stage, downloaded].filter(Boolean).join(' · ') || '处理中'}`);
    },

    /**
     * 同一预标注 job、同一阶段内进度只增不减；阶段切换/换 job 才允许重置。
     * 刷新代问和 15s worker 快照可能乱序到达，避免 chip 在 16/17 和 1/17 之间跳。
     */
    _mergePreProgress(prev, next) {
      if (!prev) return next;
      if (!next) return prev;
      const merged = { ...prev, ...next };
      const sameJob = !prev.id || !next.id || prev.id === next.id;
      const sameStage = prev.stage && next.stage && prev.stage === next.stage;
      if (sameJob && sameStage) {
        const prevDone = Number(prev.stage_done) || 0;
        const nextDone = Number(next.stage_done) || 0;
        if (nextDone < prevDone) {
          merged.stage_done = prevDone;
          merged.stage_total = prev.stage_total ?? next.stage_total;
        }
      }
      return merged;
    },

    /** 复制失败文案全文（含日志尾巴，贴出去排查用） */
    copyFailDetail() {
      this._copy([
        this.preannotateFullText,
        this.preJob && this.preJob.error && this.preJob.error.detail,
        this.errorDiagnosticsText,
      ].filter(Boolean).join('\n\n'));
    },

    /** 复制文本到剪贴板（HTTPS 下用 Clipboard API，否则降级 execCommand） */
    async _copy(text) {
      if (!text) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        this.$message.success('已复制');
      } catch {
        this.$message.warning('复制失败，请手动选中复制');
      }
    },

    /**
     * 打开提示词编辑：优先用本批快照（上次实际派发的口径），没有快照时回退注册表启用类别。
     */
    async openPromptEditor() {
      const snap = (this.preJob || {}).preact_classes;
      let rows = Array.isArray(snap)
        ? snap.map(c => ({ name: c.name, prompt: (c.prompts || []).join(' | '), enabled: true }))
        : [];
      if (rows.length === 0) {
        try {
          const res = await fetchDatasetClasses({ enabled: true });
          rows = ((res && res.data) || []).map(c => ({ name: c.name, prompt: c.prompt || '', enabled: true }));
        } catch {
          rows = [];
        }
      }
      if (rows.length === 0) {
        this.$message.warning('本批没有类别快照，注册表也没有启用类别，先到类别管理加类别');
        return;
      }
      this.promptDialog.rows = rows;
      this.promptDialog.syncRegistry = this.retry.syncRegistry;
      this.promptDialog.visible = true;
    },

    /** 应用编辑：只派发勾选的类别；留空提示词回退类别名 */
    applyPromptEdits() {
      const picked = this.promptDialog.rows.filter(r => r.enabled !== false);
      if (picked.length === 0) {
        this.$message.warning('至少保留一个参与本次重跑的类别');
        return;
      }
      this.retry.overrides = picked.map(r => ({
        name: r.name,
        prompts: String(r.prompt || '').split('|').map(s => s.trim()).filter(Boolean),
      }));
      this.retry.syncRegistry = this.promptDialog.syncRegistry;
      this.retry.overrideCount = this.retry.overrides.length;
      this.promptDialog.visible = false;
    },

    /** 重试置信度默认值：本批登记值优先，否则 0.25；输入框始终有值 */
    retryConfidenceValue() {
      const v = Number((this.preJob || {}).preact_confidence);
      return Number.isFinite(v) && v > 0 ? v : 0.25;
    },

    clearPromptEdits() {
      this.retry.overrides = null;
      this.retry.syncRegistry = false;
      this.retry.overrideCount = 0;
    },

    /** 重试可选模型：弹窗打开时取一次；取不到就只保留「沿用本批模型」 */
    async loadRetryModels() {
      const r = this.retryModels;
      if (r.loaded || r.loading) return;
      r.loading = true;
      r.error = '';
      try {
        const data = (await fetchPreannotateModels()).data || {};
        // 内置 SAM3 已由下拉的「内置 SAM3（默认）」占位，列表里不再重复一条
        r.list = (data.items || []).filter(m => m.source !== 'builtin' && m.id !== 'builtin:sam3');
        r.loaded = true;
      } catch {
        r.error = '模型清单加载失败，重试沿用本批模型';
      } finally {
        r.loading = false;
      }
    },

    retryModelLabel(model) {
      if (!model) return '';
      if (model.source === 'builtin') return `${model.name}（内置）`;
      return model.name;
    },

    /**
     * 重试预标注：确认后登记新任务（gap 补无标注 / empty 重跑未发现目标 / all 全量重跑）。
     * 任务号缺口由后端先修，候选为 0 时后端会拦下并给出原因。
     */
    async retryPreannotate() {
      if (this.retrying) return;
      const { useRegistry, overrides, syncRegistry, modelId, confidence } = this.retry;
      const scope = this.retry.scope;
      const { none, empty, total } = this.buckets;
      const rangeText = scope === 'all'
        ? `全量重跑 ${total} 张（旧预测将被覆盖，已人工标注图不动）`
        : (scope === 'empty'
          ? `重跑未发现目标 ${empty} 张（仍为空结果的保持未发现目标，已人工标注图不动）`
          : `仅补缺口 ${none} 张`);
      const classText = overrides
        ? `按改后的 ${overrides.length} 个类别派发${syncRegistry ? '（同时写回注册表）' : '（不回写注册表）'}`
        : (useRegistry ? '最新类别注册表' : '沿用本批快照（无快照时用注册表）');
      const pickedModel = this.retryModels.list.find(m => m.id === modelId);
      const modelText = modelId
        ? `改用 ${this.retryModelLabel(pickedModel) || modelId}`
        : '沿用本批模型';
      const confidenceText = confidence != null
        ? `\n置信度：${confidence}`
        : '';
      try {
        await this.$confirm(
          `将登记新的预标注任务：\n范围：${rangeText}\n模型：${modelText}${confidenceText}\n类别：${classText}\n任务执行期间本按钮不可再用。`,
          '重试预标注',
          { type: 'warning', confirmButtonText: '登记任务', cancelButtonText: '取消' },
        );
      } catch {
        return; // 取消
      }
      this.retrying = true;
      try {
        const payload = { batch: this.batch.batch, scope, use_registry: useRegistry };
        if (modelId) payload.preact_model_id = modelId; // 空＝沿用本批快照
        if (confidence != null) payload.confidence = confidence; // YOLO 置信度：空＝沿用本批
        if (overrides) {
          payload.preact_classes = overrides; // 按次改的提示词：后端按它派发，可选写回注册表
          payload.sync_registry = syncRegistry;
        }
        const res = await retryDatasetPreannotate(payload);
        const d = res.data || {};
        const rp = d.repaired || {};
        const repairText = (Number(rp.backfilled) > 0 || Number(rp.imported) > 0)
          ? `，回填任务号 ${rp.backfilled || 0}、补导入 ${rp.imported || 0}`
          : '';
        const syncText = Number(d.registry_synced) > 0 ? `，已写回注册表 ${d.registry_synced} 类` : '';
        this.$message.success(`已登记重试任务：候选 ${d.candidates || 0} 张${repairText}${syncText}`);
        this.clearPromptEdits(); // 派发后清掉临时改动，避免下一次误带
        this.retry.modelId = '';
        this.retry.confidence = this.retryConfidenceValue();
        this.$emit('refresh');
      } catch {
        // 错误已由拦截器统一提示（409 在途 / 400 无类别 / 404 无项目）
      } finally {
        this.retrying = false;
      }
    },

  },
};
</script>

<style scoped>
/* 状态 chip：整行流水线的当前状态 */
.pipe-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 1px 10px;
  height: 22px;
  border: 1px solid #DCDFE6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #606266;
}

.pipe-chip .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
  background: #C0C4CC;
}

.pipe-chip .arr { color: #C0C4CC; font-size: 11px; }

.pipe-chip.blue { border-color: #B3D8FF; background: #ECF5FF; color: #409EFF; }
.pipe-chip.blue .dot { background: #409EFF; }
.pipe-chip.green { border-color: #C2E7B0; background: #F0F9EB; color: #67C23A; }
.pipe-chip.green .dot { background: #67C23A; }
.pipe-chip.orange { border-color: #F5DAB1; background: #FDF6EC; color: #E6A23C; }
.pipe-chip.orange .dot { background: #E6A23C; }
.pipe-chip.red { border-color: #FBC4C4; background: #FEF0F0; color: #F56C6C; }
.pipe-chip.red .dot { background: #F56C6C; }

.pipe-chip.pulse .dot { animation: chip-pulse 1.2s ease-in-out infinite; }
@keyframes chip-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .25; }
}

/* 二级弹窗 */
.pd-title { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; }
.pd-count { color: #909399; font-size: 12px; font-weight: 400; }
.pd-foot { display: flex; flex-wrap: wrap; gap: 8px; justify-content: space-between; align-items: center; }
.pd-foot-right { display: flex; gap: 8px; }

/* 批次重做：入口按钮行 + 弹窗内容 */
.admin-row { display: flex; align-items: center; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.admin-row .danger-btn { color: #F56C6C; border-color: #FBC4C4; }
.admin-lead { color: #606266; font-size: 13px; line-height: 20px; margin-bottom: 10px; }
.admin-form { margin-top: 8px; }
.admin-warn {
  background: #FEF0F0; color: #F56C6C; font-size: 12px;
  padding: 8px 10px; border-radius: 4px; margin: 8px 0; line-height: 18px;
}
.admin-hint { color: #909399; font-size: 12px; line-height: 18px; }
.admin-film { display: flex; flex-wrap: wrap; gap: 6px; max-height: 168px; overflow-y: auto; }
.admin-film-item {
  position: relative; width: 72px; height: 54px; border: 2px solid #DCDFE6; border-radius: 4px;
  overflow: hidden; cursor: pointer; background: #F5F7FA; flex: none;
}
.admin-film-item img { width: 100%; height: 100%; object-fit: cover; display: block; }
.admin-film-item.selected { border-color: #409EFF; }
.admin-film-check {
  position: absolute; right: 2px; bottom: 2px; background: #409EFF; color: #fff;
  font-size: 11px; line-height: 14px; padding: 0 3px; border-radius: 2px;
}
.admin-film-ph {
  display: flex; align-items: center; justify-content: center;
  height: 100%; color: #909399; font-size: 12px;
}
.admin-confirm { margin-top: 10px; font-size: 12px; color: #606266; }
.admin-loading { color: #909399; font-size: 12px; padding: 12px 0; }
.danger-text { color: #F56C6C; }

/* 三段进度条 */
.stagebar { margin: 4px 0 16px; }
.stagebar .prow { display: flex; align-items: center; }
.stagebar .seg { flex: 1; height: 4px; background: #DCDFE6; border-radius: 2px; }
.stagebar .seg.done { background: #67C23A; }
.stagebar .seg.cur { background: #409EFF; }
.stagebar .node {
  width: 13px; height: 13px; border-radius: 50%; background: #fff;
  border: 2px solid #DCDFE6; flex: none; margin-left: -2px; box-sizing: border-box; z-index: 1;
}
.stagebar .node.done { border-color: #67C23A; background: #67C23A; }
.stagebar .node.cur { border-color: #409EFF; background: #fff; box-shadow: 0 0 0 4px #ECF5FF; }
.stagebar .node.warn { border-color: #E6A23C; background: #E6A23C; }
.stagebar .plabels { display: flex; margin-top: 4px; font-size: 12px; color: #909399; }
.stagebar .plabel { flex: 1; white-space: nowrap; }
.stagebar .plabel:nth-child(1) { text-align: left; }
.stagebar .plabel:nth-child(2) { text-align: center; }
.stagebar .plabel:nth-child(3) { text-align: right; }
.stagebar .plabel.done { color: #67C23A; }
.stagebar .plabel.cur { color: #409EFF; }
.stagebar .plabel.warn { color: #E6A23C; }

.stage-detail { border: 1px solid #EBEEF5; border-radius: 4px; padding: 10px 12px; margin-bottom: 10px; text-align: left; }
.stage-detail .sd-head { display: flex; align-items: center; gap: 8px; font-weight: 600; margin-bottom: 6px; }
.stage-detail .sd-head .idx {
  width: 18px; height: 18px; border-radius: 50%; background: #C0C4CC;
  color: #fff; font-size: 11px; line-height: 18px; text-align: center; flex: none;
}
.stage-detail.done .sd-head .idx { background: #67C23A; }
.stage-detail.cur .sd-head .idx { background: #409EFF; }
.stage-detail.warn .sd-head .idx { background: #E6A23C; }
.stage-detail .line { color: #606266; font-size: 12px; margin: 2px 0; }
.stage-detail .line b { color: #303133; }
.stage-detail .hint { color: #909399; font-size: 12px; margin-top: 4px; }
.stage-detail .err { color: #F56C6C; font-size: 12px; margin-top: 4px; }
.stage-detail .note { color: #E6A23C; }
/* 失败文案带日志尾巴：保留换行，长路径/栈帧强制断行，不撑破弹窗 */
.pre-status { white-space: pre-wrap; word-break: break-all; }
.diag-box {
  max-height: 180px; overflow: auto; margin: 4px 0 0; padding: 6px 8px;
  background: #fff; border: 1px solid #FDE2E2; border-radius: 4px;
  color: #606266; font-size: 11px; line-height: 1.5; white-space: pre-wrap; word-break: break-all;
}
.progress-steps { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: 6px 0; color: #909399; font-size: 12px; }
.progress-step.done { color: #67C23A; }
.progress-step.current { color: #409EFF; font-weight: 600; }
.progress-step.failed { color: #F56C6C; font-weight: 600; }
.progress-arrow { color: #C0C4CC; margin-left: 6px; }
.error-line { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: 4px 0; }
.error-line .err-title { color: #F56C6C; font-size: 12px; }
.error-detail { color: #606266; font-size: 12px; }
.detail-row { display: flex; gap: 10px; padding: 4px 0; border-bottom: 1px solid #F2F6FC; }
.detail-row span { width: 64px; color: #909399; flex: none; }
.detail-row b { font-weight: 500; word-break: break-all; }
.track-note { color: #909399; font-size: 12px; margin-left: 8px; }

/* 预标注缺口：原因 + 重试范围/类别选项 + 重试按钮 */
.retry-box {
  margin-top: 6px;
  padding: 8px 10px;
  border: 1px solid #F5DAB1;
  border-radius: 4px;
  background: #FDF6EC;
}
.retry-box .warn-text { color: #E6A23C; font-size: 12px; }
.retry-box .retry-opts {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}
.retry-box .reg-check { margin-left: 4px; }
.retry-box .hint { color: #909399; font-size: 12px; margin-top: 6px; }
.retry-box .muted { color: #909399; font-size: 12px; }
.retry-box .undo-edit { padding: 0 4px; }
.prompt-hint { font-size: 12px; line-height: 18px; margin-bottom: 8px; }

.mini-progress {
  height: 6px; border-radius: 3px; background: #EBEEF5; overflow: hidden;
  display: flex; margin: 6px 0 2px;
}
.mini-progress .p1 { background: #67C23A; }
.mini-progress .p2 { background: #409EFF; }
.mini-progress .p3 { background: #E6A23C; }


.pd-more { margin-left: auto; }

.redo-bar {
  display: flex; align-items: center; gap: 2px;
  margin: 0 0 10px; padding-bottom: 8px; border-bottom: 1px dashed #EBEEF5;
}
.redo-bar .muted { margin-right: 4px; }

.redo-lock { margin-left: auto; color: #E6A23C; font-size: 12px; }
</style>

<!-- 图片列表弹窗：固定高度 80vh，内容超出时内部滚动
     （dialog 是 Element 渲染并 append-to-body，不带组件 scoped 属性，必须放全局块） -->
<style>
.images-dialog {
  height: 80vh;
  display: flex;
  flex-direction: column;
  margin: 10vh auto !important;
}
.images-dialog > .el-dialog__header { flex: none; }
.images-dialog > .el-dialog__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
</style>
